// Generic data-dws-* behaviours, one delegated document listener each: form lock, tree type-ahead, prevented keys,
// shortcut buttons, modified link clicks, DwsLink's href fallback, keep-focus, aria-describedby hosts, keyboard
// activation, focus rescue, context-menu pass-through, DwsNavMenu's arrow keys and escape-clears.
// Core: the markup carries the attributes and nothing calls in first.

import { publish } from "../shared/publish";

// DwsForm while its submit handler runs (data-dws-form-locked on the fieldset): the fields stay focusable, so the focus
// is not dropped, but nothing in them may change and no button may be pressed. Tab, Shift+Tab and Escape pass.
(function () {
    function locked(e) {
        return e.target instanceof Element && e.target.closest('[data-dws-form-locked="true"]') !== null;
    }

    function refuse(e) {
        if (locked(e)) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    }

    window.addEventListener('keydown', function (e) {
        if (e.key === 'Tab' || e.key === 'Escape' || e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') {
            return;
        }

        refuse(e);
    }, true);

    ['beforeinput', 'click', 'dblclick', 'paste', 'cut', 'drop'].forEach(function (type) {
        window.addEventListener(type, refuse, true);
    });

    // A native select opens on mousedown, a range input moves on it.
    window.addEventListener('mousedown', function (e) {
        if (locked(e) && /^(SELECT|INPUT|OPTION)$/.test((e.target as Element).tagName)) {
            e.preventDefault();
        }
    }, true);
})();

// Type-ahead in a tree (DwsTreeview, data-dws-tree-typeahead): a printable key on a focused node moves focus to the
// next visible node whose label (data-dws-typeahead-text) starts with what was typed. Keys typed within 700 ms are
// one text; the same letter repeated cycles through the nodes starting with it. Only keys whose target is the node
// itself count, so a rename box or a text field inside a node keeps its typing. The node's own focus handler tells
// .NET where the focus went.
(function () {
    let typed = '';
    let typedAt = 0;

    document.addEventListener('keydown', function (e) {
        const row = e.target as HTMLElement;

        if (!(row instanceof Element) || row.getAttribute('role') !== 'treeitem' || !row.hasAttribute('data-dws-typeahead-text')) {
            return;
        }

        if (e.defaultPrevented || e.ctrlKey || e.altKey || e.metaKey || typeof e.key !== 'string' || e.key.length !== 1 || e.key === ' ' || e.key === '*') {
            return;
        }

        let tree = row.closest('[data-dws-tree-typeahead]');

        if (!tree) {
            return;
        }

        let now = Date.now();
        typed = now - typedAt > 700 ? e.key : typed + e.key;
        typedAt = now;

        let text = typed.toLowerCase();
        let single = true;

        for (let c = 1; c < text.length; c++) {
            if (text.charAt(c) !== text.charAt(0)) {
                single = false;
                break;
            }
        }

        let prefix = single ? text.charAt(0) : text;
        let rows = Array.prototype.filter.call(tree.querySelectorAll('[role="treeitem"][data-dws-typeahead-text]'), function (candidate) {
            return candidate.closest('[data-dws-tree-typeahead]') === tree && candidate.getClientRects().length > 0;
        });
        let index = rows.indexOf(row);
        // One letter (also repeated) looks from the next node on; a longer text stays on a node that still matches.
        let start = single ? index + 1 : index;

        for (let i = 0; i < rows.length; i++) {
            let candidate = rows[(start + i) % rows.length];

            if ((candidate.getAttribute('data-dws-typeahead-text') || '').toLowerCase().indexOf(prefix) === 0) {
                if (candidate !== row) {
                    candidate.focus();
                }

                break;
            }
        }
    });
})();

// data-dws-prevent-keys="ctrl+z ctrl+s ...": key combinations a component handles itself (DwsInput's undo/redo and
// KeyboardShortcuts), whose browser default is cancelled. Same combo format as DwsInput.BuildShortcutCombo.
(function () {
    document.addEventListener('keydown', function (e) {
        const target = e.target as HTMLElement;

        const host = target instanceof Element ? target.closest('[data-dws-prevent-keys]') : null;

        if (!host || typeof e.key !== 'string') {
            return;
        }

        // A text field inside a host (an inline editor in a tree row) keeps its keys; only the host's own keys count.
        if (host !== target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) {
            return;
        }

        // A button or link inside a host (a banner's close button, a card header action) keeps Space / Enter:
        // cancelling the keydown cancels its activation, so it stopped answering the keyboard.
        if (host !== target && (e.key === ' ' || e.key === 'Enter') && /^(BUTTON|A|SUMMARY)$/.test(target.tagName)) {
            return;
        }

        let parts = [];
        if (e.ctrlKey) parts.push('ctrl');
        if (e.altKey) parts.push('alt');
        if (e.shiftKey) parts.push('shift');
        if (e.metaKey) parts.push('meta');
        parts.push(e.key === ' ' ? 'space' : e.key.toLowerCase());

        let combos = host.getAttribute('data-dws-prevent-keys').split(' ');

        if (combos.indexOf(parts.join('+')) >= 0) {
            e.preventDefault();
        }
    });
})();

// data-dws-shortcut="ctrl+z meta+z" on a button inside an element marked data-dws-shortcut-scope (DwsFormBuilder's
// Undo / Redo): the key combination, pressed anywhere in the scope, clicks the button - key and button share one
// .NET handler, and no other key press costs a round trip. Same combo format as data-dws-prevent-keys: the modifiers
// in the order ctrl, alt, shift, meta, then the key in lower case ("shift+meta+z"). A text field keeps its keys (its
// own undo), and so does a disabled button. A shortcut may also be a key without Ctrl / Alt / Cmd (DwsDataGrid's
// "f" opens its search panel): such a key is what typing is made of, so every form control keeps it - also a select
// (type-ahead), a check box or a slider - and a key that is held down counts once.
(function () {
    let lastPointerTarget: Element | null = null;

    document.addEventListener('pointerdown', function (e) {
        lastPointerTarget = e.target instanceof Element ? e.target : null;
    }, true);

    document.addEventListener('keydown', function (e) {
        const target = e.target as HTMLInputElement;

        if (e.defaultPrevented || typeof e.key !== 'string' || !(target instanceof Element)) {
            return;
        }

        let modified = e.ctrlKey || e.metaKey || e.altKey;

        if (!modified && (e.repeat || e.isComposing || /^(INPUT|SELECT)$/.test(target.tagName))) {
            return;
        }

        // ...and so does a list, a menu or a combo box built from other elements: letters are their type-ahead.
        if (!modified && target.closest('[role="listbox"], [role="menu"], [role="combobox"], [role="textbox"], [role="searchbox"]')) {
            return;
        }

        let scope = target.closest('[data-dws-shortcut-scope]');

        // A click on something that takes no focus (a grid body cell) leaves focus on an ancestor (<main>, <body>),
        // outside the scope. The scope still counts when the last pointer press landed inside it and focus is still
        // on an ancestor of that spot - keyboard focus moved anywhere else means the user has left.
        if (!scope && lastPointerTarget && lastPointerTarget.isConnected && target.contains(lastPointerTarget)) {
            scope = lastPointerTarget.closest('[data-dws-shortcut-scope]');
        }

        if (!scope) {
            return;
        }

        if (target.isContentEditable || target.tagName === 'TEXTAREA'
            || (target.tagName === 'INPUT' && !/^(checkbox|radio|button|submit|reset|range|color|file|image)$/i.test(target.type))) {
            return;
        }

        let parts = [];
        if (e.ctrlKey) parts.push('ctrl');
        if (e.altKey) parts.push('alt');
        if (e.shiftKey) parts.push('shift');
        if (e.metaKey) parts.push('meta');
        parts.push(e.key === ' ' ? 'space' : e.key.toLowerCase());

        let combo = parts.join('+');
        const buttons = scope.querySelectorAll<HTMLButtonElement>('[data-dws-shortcut]');

        for (let i = 0; i < buttons.length; i++) {
            let button = buttons[i];

            if (!button.disabled && button.getAttribute('data-dws-shortcut').split(' ').indexOf(combo) >= 0) {
                e.preventDefault();
                button.click();

                // Reached through the pointer fallback, focus was outside the scope: move it onto the button, so
                // the keys that follow (Escape closing the panel just opened) reach the component's own handlers.
                if (!scope.contains(target)) {
                    button.focus({ preventScroll: true });
                }

                return;
            }
        }
    });
})();

// A link marked data-dws-native-modified-click (DwsLink to an internal page, which navigates through .NET and so
// cancels the click) is left to the browser when Ctrl, Cmd or Shift is held: those clicks mean "open in a new tab /
// window", and cancelling them navigated the current tab instead. Stopped in the capture phase on the window, before
// Blazor's document-level handler can cancel the default.
(function () {
    window.addEventListener('click', function (e) {
        if (!(e.ctrlKey || e.metaKey || e.shiftKey)) {
            return;
        }

        let target = e.target;
        let link = target instanceof Element ? target.closest('a[data-dws-native-modified-click][href]') : null;

        if (link) {
            e.stopPropagation();
        }
    }, true);
})();

// DwsLink, href fallback (Blazor Server). An internal link navigates through .NET, so its click is cancelled; with
// the connection gone nothing answered and the link did nothing. A link marked data-dws-href-fallback="<ms>" is
// followed by the browser instead (a full page load of its href) when
//   - the reconnect display is showing at the moment of the click: the click is stopped before Blazor's document
//     handler can cancel it, so it is an ordinary link click; or
//   - .NET does not acknowledge the click (dwsLinkAcknowledgeClick, called first thing by the component's handler)
//     within <ms> milliseconds. 0 switches this wait off.
// Nothing is loaded while the browser reports being offline: there is nothing to load, and the page in front of the
// user is worth more than the browser's error page.
(function () {
    let pending = null;

    function clearPending() {
        if (pending) {
            window.clearTimeout(pending.timer);
            pending = null;
        }
    }

    function reconnectDisplayShown() {
        let display = document.getElementById('components-reconnect-modal');

        if (!display) {
            return false;
        }

        let classes = display.classList;

        if (classes.contains('components-reconnect-show') || classes.contains('components-reconnect-failed')
            || classes.contains('components-reconnect-rejected') || classes.contains('components-reconnect-retrying')
            || classes.contains('components-reconnect-paused') || classes.contains('components-reconnect-resume-failed')) {
            return true;
        }

        // The framework's default display carries no state classes: it is shown and hidden through its style.
        if (display.shadowRoot || display.tagName === 'DIALOG') {
            if (display instanceof HTMLDialogElement && !display.open) {
                return false;
            }

            let style = window.getComputedStyle(display);
            return style.display !== 'none' && style.visibility !== 'hidden';
        }

        return false;
    }

    publish("dwsLinkAcknowledgeClick", function (): void {
        clearPending();
    });

    // For tests and diagnostics: whether a click is waiting for its acknowledgement.
    publish("dwsLinkHasPendingClick", function (): boolean {
        return pending !== null;
    });

    window.addEventListener('click', function (e) {
        if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.defaultPrevented) {
            return;
        }

        let target = e.target;
        const link = target instanceof Element ? target.closest<HTMLAnchorElement>('a[data-dws-href-fallback][href]') : null;

        if (!link || (link.target && link.target !== '_self')) {
            return;
        }

        if (reconnectDisplayShown()) {
            clearPending();
            e.stopPropagation();
            return;
        }

        let delay = parseInt(link.getAttribute('data-dws-href-fallback'), 10);

        if (!(delay > 0)) {
            return;
        }

        clearPending();

        let href = link.href;
        let from = window.location.href;

        pending = {
            timer: window.setTimeout(function () {
                pending = null;

                // Already somewhere else: the navigation happened and only the acknowledgement was lost.
                if (window.location.href !== from || navigator.onLine === false) {
                    return;
                }

                window.location.assign(href);
            }, delay)
        };
    }, true);

    // A page that leaves (or is restored from the back/forward cache) has no click to wait for.
    window.addEventListener('pagehide', clearPending);
})();

// Elements marked with data-dws-keep-focus (suggestion lists and the buttons next to a text field) keep the focus where it
// is when pressed: the mousedown would otherwise blur the field, which closes the list before the click on an option
// arrives. Blazor's @onmousedown:preventDefault cancels nothing on an element without a .NET @onmousedown handler.
(function () {
    document.addEventListener('mousedown', function (e) {
        let target = e.target;

        if (target instanceof Element && target.closest('[data-dws-keep-focus]')) {
            e.preventDefault();
        }
    });
})();

// A wrapper marked data-dws-describedby="<id>" (DwsTooltip around its trigger) adds that id to the aria-describedby of
// whatever element inside it takes focus, and takes it off again on blur: the trigger is the consumer's markup, which the
// component cannot annotate itself, so a screen reader never heard the tooltip.
(function () {
    function describer(target: EventTarget | null): Element | null {
        return target instanceof Element ? target.closest('[data-dws-describedby]') : null;
    }

    function ids(element: Element): string[] {
        let value = element.getAttribute('aria-describedby');
        return value ? value.split(/\s+/).filter(function (id) { return id.length > 0; }) : [];
    }

    document.addEventListener('focusin', function (e) {
        let host = describer(e.target);

        if (!host || e.target === host) {
            return;
        }

        let id = host.getAttribute('data-dws-describedby');
        const field = e.target as Element;
        const current = ids(field);

        if (id && current.indexOf(id) < 0) {
            current.push(id);
            field.setAttribute('aria-describedby', current.join(' '));
        }
    });

    document.addEventListener('focusout', function (e) {
        let host = describer(e.target);

        if (!host) {
            return;
        }

        let id = host.getAttribute('data-dws-describedby');
        const field = e.target as Element;
        const remaining = ids(field).filter(function (existing) { return existing !== id; });

        if (remaining.length > 0) {
            field.setAttribute('aria-describedby', remaining.join(' '));
        } else {
            field.removeAttribute('aria-describedby');
        }
    });
})();

// Elements marked with data-dws-activate (a clickable DwsPaper) answer the keyboard like a native button: Enter clicks
// on keydown, Space on keyup. Only a key pressed on the element itself counts, so Enter in an input inside a
// clickable paper does not click the paper. One listener on the document serves every element, so nothing has to be
// registered or cleaned up per component.
(function () {
    function isActivationTarget(e) {
        let target = e.target;

        return !!target && typeof target.hasAttribute === 'function' && target.hasAttribute('data-dws-activate');
    }

    function isSpace(e) {
        return e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space';
    }

    document.addEventListener('keydown', function (e) {
        if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || !isActivationTarget(e)) {
            return;
        }

        if (e.key === 'Enter') {
            e.preventDefault();

            if (!e.repeat) {
                (e.target as HTMLElement).click();
            }
        } else if (isSpace(e)) {
            // Keeps the page from scrolling; the click follows on keyup.
            e.preventDefault();
        }
    });

    document.addEventListener('keyup', function (e) {
        if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || !isActivationTarget(e) || !isSpace(e)) {
            return;
        }

        e.preventDefault();
        (e.target as HTMLElement).click();
    });

    // A clickable surface (role=button) must not contain interactive content: screen readers flatten it and a click on
    // an inner control bubbles to the surface. Warn once per element, the first time it takes focus, so the mistake
    // shows up in development without a scan of every element on every render.
    let dwsActivateChecked = typeof WeakSet === 'function' ? new WeakSet() : null;
    let dwsInteractiveSelector = 'a[href], button, input, select, textarea, [contenteditable="true"], [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])';

    document.addEventListener('focusin', function (e) {
        const target = e.target as HTMLElement;

        if (!dwsActivateChecked || !isActivationTarget(e) || dwsActivateChecked.has(target)) {
            return;
        }

        dwsActivateChecked.add(target);

        let nested = target.querySelector(dwsInteractiveSelector);

        if (nested) {
            console.warn('DWS: a clickable element (role=button) contains interactive content (' + nested.tagName.toLowerCase() + '). Screen readers flatten it and clicks on it also click the surface. Remove Clickable/OnClick or move the control out.', target);
        }
    });
})();

// Focus rescue. When the focused element is removed from the page (a "Try again" button that goes with its error
// panel, a toast's close button, a file's "Remove" button, a grid row action) the browser drops focus to <body> and a
// keyboard user starts over at the top of the page. Components that know a better target move focus themselves; this
// only steps in when focus is still on <body> a moment later, and moves it to the first focusable element of the
// nearest container that is still on the page - never higher than the page content, so navigation is not affected.
(function () {
    let focusable = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let stop = 'body, main, .basepage, .dws-main-content, [role="dialog"]';
    let lastFocused: Element | null = null;
    let lastAncestors: Element[] = [];
    let pending = false;

    function isUsable(el: HTMLElement): boolean {
        return el.tabIndex >= 0 && el.getClientRects().length > 0 && !el.closest('[inert]') && !el.closest('[aria-hidden="true"]');
    }

    function rescue() {
        pending = false;

        let active = document.activeElement;

        if (!lastFocused || lastFocused.isConnected || (active && active !== document.body)) {
            return;
        }

        for (let i = 0; i < lastAncestors.length; i++) {
            let ancestor = lastAncestors[i];

            if (!ancestor.isConnected) {
                continue;
            }

            if (ancestor.matches(stop)) {
                return;
            }

            const candidates = ancestor.querySelectorAll<HTMLElement>(focusable);

            for (let j = 0; j < candidates.length; j++) {
                if (isUsable(candidates[j])) {
                    candidates[j].focus({ preventScroll: true });
                    lastFocused = null;
                    return;
                }
            }
        }
    }

    document.addEventListener('focusin', function (e) {
        lastFocused = e.target instanceof Element ? e.target : null;
        lastAncestors = [];

        let parent = lastFocused ? lastFocused.parentElement : null;

        while (parent && parent !== document.documentElement) {
            lastAncestors.push(parent);
            parent = parent.parentElement;
        }
    }, true);

    new MutationObserver(function () {
        if (!pending && lastFocused && !lastFocused.isConnected) {
            pending = true;
            setTimeout(rescue, 350);
        }
    }).observe(document.documentElement, { childList: true, subtree: true });
})();

// data-dws-pass-contextmenu on a menu's click-catching overlay: a right-click on it closes the menu (the component's own
// handler) and is then passed to whatever lies under the pointer, so right-clicking another row moves the menu there
// instead of only closing it.
(function () {
    document.addEventListener('contextmenu', function (e) {
        const overlay = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-dws-pass-contextmenu]') : null;

        if (!overlay || !e.isTrusted) {
            return;
        }

        let previous = overlay.style.pointerEvents;
        overlay.style.pointerEvents = 'none';
        let below = document.elementFromPoint(e.clientX, e.clientY);
        overlay.style.pointerEvents = previous;

        if (!below || below === overlay) {
            return;
        }

        let x = e.clientX;
        let y = e.clientY;

        setTimeout(function () {
            if (below.isConnected) {
                below.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: x, clientY: y, button: 2 }));
            }
        }, 60);
    }, true);
})();

// DwsNavMenu: arrow keys between the visible entries (links and group headers) - with ~150 entries Tab alone was slow.
// Vertical menu: Up/Down move, Home/End jump, Right opens a closed group header, Left closes an open one or moves to the
// header of the group the entry is in. Horizontal menu: Left/Right move (mirrored right-to-left). Tab still walks every
// entry, so nothing changes for users who do not know the keys. Entries inside collapsed groups have no tabindex 0 and
// no layout box, so they are skipped.

document.addEventListener('keydown', function (e) {
    const keyTarget = e.target as HTMLElement;

    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || !keyTarget || !keyTarget.closest) {
        return;
    }

    const entry = keyTarget.closest<HTMLElement>('.dws-nav-menu .dws-list-item');
    let menu = entry ? entry.closest('.dws-nav-menu') : null;

    if (!entry || entry !== keyTarget || !menu) {
        return;
    }

    let horizontal = menu.classList.contains('dws-nav-menu-horizontal');
    let rtl = getComputedStyle(menu).direction === 'rtl';
    let next = horizontal ? (rtl ? 'ArrowLeft' : 'ArrowRight') : 'ArrowDown';
    let prev = horizontal ? (rtl ? 'ArrowRight' : 'ArrowLeft') : 'ArrowUp';
    const entries: HTMLElement[] = Array.prototype.filter.call(menu.querySelectorAll('.dws-list-item[tabindex="0"]'), function (el: HTMLElement) {
        return el.offsetParent !== null && el.closest('.dws-nav-menu') === menu && !el.closest('[aria-hidden="true"]');
    });
    let index = entries.indexOf(entry);
    let target: HTMLElement | null = null;

    if (e.key === next) {
        target = entries[Math.min(index + 1, entries.length - 1)];
    } else if (e.key === prev) {
        target = entries[Math.max(index - 1, 0)];
    } else if (e.key === 'Home') {
        target = entries[0];
    } else if (e.key === 'End') {
        target = entries[entries.length - 1];
    } else if (!horizontal && e.key === 'ArrowRight' && entry.getAttribute('aria-expanded') === 'false') {
        e.preventDefault();
        entry.click();
        return;
    } else if (!horizontal && e.key === 'ArrowLeft') {
        if (entry.getAttribute('aria-expanded') === 'true') {
            e.preventDefault();
            entry.click();
            return;
        }

        let group = entry.closest('[role="group"]');
        const header = group && group.id ? menu.querySelector<HTMLElement>('[aria-controls="' + group.id + '"]') : null;
        target = header;
    }

    if (target && target !== entry) {
        e.preventDefault();
        target.focus();
    } else if (target) {
        e.preventDefault();
    }
});

// data-dws-escape-clears on a container: Escape in a non-empty text field inside it empties the field, like a native
// type="search" input, and fires input + change so Blazor's bindings see the new value. A second Escape on the empty
// field is left to the page (closing a dialog, a panel, ...). Used by the DwsDataGrid search panel.

document.addEventListener('keydown', function (e) {
    const field = e.target as HTMLInputElement;

    if (e.key !== 'Escape' || e.defaultPrevented || !field || !field.closest || !field.closest('[data-dws-escape-clears]')) {
        return;
    }

    let isTextField = field.tagName === 'TEXTAREA'
        || (field.tagName === 'INPUT' && /^(text|search|email|tel|url|number|)$/.test(field.type || ''));

    if (!isTextField || field.readOnly || field.disabled || field.value === '') {
        return;
    }

    e.preventDefault();
    e.stopPropagation();
    field.value = '';
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
});
